import type { GdeEditorImageSourceUrls } from '@shared/gde-ckeditor/classes/gde-editor-images';

import type { CoverCardState } from '@shared/utils/cover-card-state.util';

import type { ActivityCover } from '@api';

import type { CardCover, CardCoverInfo } from '../models/activity-card.model';

/** The public path prefix every stored KT image source starts with; a constant fixed by ADR 24. */
export const KT_IMAGE_PUBLIC_PATH = '/images/';

/** Client twin of the contract's `StickerImagePath` pattern (activity-templates.tsp); change it only with the contract. */
export const KT_MEDIA_PATH_PATTERN = /^\/images\/([A-Za-z0-9_~-]+\/)*[A-Za-z0-9_~-]+\.(jpg|png)$/;

/** Client twin of the section image-source policy: the public `/images/` path plus the tenant/owner/id key layout (ADR 24). */
export const KT_IMAGE_SOURCE_PATTERN =
  /^\/images\/(~no-tenant|[A-Za-z0-9_-]{1,64})\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{32}\.(jpg|png|gif|webp)$/;

/**
 * The cover an activity card renders: the cover's media URL with its framing.
 *
 * @param cover The activity's cover as the API returns it, if any.
 * @param mediaBaseUrl The environment's media origin.
 * @returns The card cover, or `null` when there is no cover or its path cannot be loaded, so the card keeps its
 *   type icon and colour.
 */
export function ktCardCover(cover: ActivityCover | null, mediaBaseUrl: string): CardCover | null {
  if (cover === null) {
    return null;
  }

  const src = ktMediaPathDisplayUrl(cover.imageUrl, mediaBaseUrl);

  return src === null ? null : { src, focalX: cover.focalX, focalY: cover.focalY, zoom: cover.zoom };
}

/**
 * The card's cover input: the cover resolved by {@link ktCardCover}, with the status marker unchanged.
 *
 * @param state The activity's cover state as the queue answers it.
 * @param mediaBaseUrl The environment's media origin.
 * @returns The cover info for the card.
 */
export function ktCardCoverInfo(state: CoverCardState, mediaBaseUrl: string): CardCoverInfo {
  return { image: ktCardCover(state.cover, mediaBaseUrl), status: state.status };
}

/**
 * The URL a stored KT image source loads from: the media distribution serves the published images at its root, so
 * `/images/{rest}` becomes `{mediaBaseUrl}/{rest}` (ADR 24). Mirrors the backend's `SectionImageUrls.ToAbsolute`.
 *
 * @param source An `img[src]` value as stored.
 * @param mediaBaseUrl The environment's media origin; when empty, `source` is returned unchanged.
 * @returns The media URL for a KT image source, and any other source unchanged.
 */
export function ktImageDisplayUrl(source: string, mediaBaseUrl: string): string {
  if (!mediaBaseUrl || !KT_IMAGE_SOURCE_PATTERN.test(source)) {
    return source;
  }

  return `${mediaBaseUrl}/${source.slice(KT_IMAGE_PUBLIC_PATH.length)}`;
}

/**
 * The editor's view of KT image sources on the given media origin.
 *
 * @param mediaBaseUrl The environment's media origin, or empty when unset.
 * @returns Mappings between stored sources and media URLs.
 */
export function ktImageSourceUrls(mediaBaseUrl: string): GdeEditorImageSourceUrls {
  return {
    toDisplayUrl: (source) => ktImageDisplayUrl(source, mediaBaseUrl),
    toStoredSource: (url) => ktImageStoredSource(url, mediaBaseUrl),
  };
}

/**
 * The stored KT image source a media URL stands for — the inverse of {@link ktImageDisplayUrl}. Mirrors the
 * backend's `SectionImageUrls.ToRelative`.
 *
 * @param url An absolute URL.
 * @param mediaBaseUrl The environment's media origin; when empty, nothing is the media host's.
 * @returns The stored source, or `null` when `url` is not a KT image on the media origin.
 */
export function ktImageStoredSource(url: URL, mediaBaseUrl: string): string | null {
  if (!mediaBaseUrl || url.origin !== mediaBaseUrl || url.search !== '' || url.hash !== '') {
    return null;
  }

  const source = KT_IMAGE_PUBLIC_PATH + url.pathname.slice(1);

  return KT_IMAGE_SOURCE_PATTERN.test(source) ? source : null;
}

/**
 * The URL an opaque media path loads from, e.g. the `stickerImageUrl` of an activity. Never reads tenant, owner, kind
 * or id out of the path; only the contract's `StickerImagePath` shape (`.jpg` / `.png`) is checked. Widening it means
 * changing the contract first.
 *
 * @param path A media path as the API returns it.
 * @param mediaBaseUrl The environment's media origin.
 * @returns `{mediaBaseUrl}/…` for a valid path, or `null` when the path is invalid or `mediaBaseUrl` is empty.
 */
export function ktMediaPathDisplayUrl(path: string, mediaBaseUrl: string): string | null {
  if (!mediaBaseUrl || !KT_MEDIA_PATH_PATTERN.test(path)) {
    return null;
  }

  return `${mediaBaseUrl}/${path.slice(KT_IMAGE_PUBLIC_PATH.length)}`;
}
